import type {
  PersonalSpacesRow,
  PersonalSpacesSkipReason,
  PersonalSpacesSource,
  PersonalSpacesStatus,
} from "@/api/admin-api"

export const PERSONAL_SPACES_SOURCES: readonly PersonalSpacesSource[] = [
  "onedrive",
  "mail",
  "chats",
] as const

export const PERSONAL_SPACES_SKIP_REASONS: readonly PersonalSpacesSkipReason[] = [
  "unsupported_type",
  "oversize",
  "labelled_or_encrypted",
  "chat_linked_file",
  "meeting_chat",
  "not_owned",
  "inline_or_small_image",
  "storage_cap",
  "other",
] as const

/** Rows rendered before "Show more"; the summary itself already holds every row. */
export const PERSONAL_SPACES_PAGE_SIZE = 50

/** FILTH raises the freshness alert at 90 minutes (spec 9.3); the card only names it. */
export const PERSONAL_SPACES_FRESHNESS_ALERT_MINUTES = 90

type BadgeVariant = "success" | "warning" | "error" | "secondary" | "outline"

export function personalSpacesStatusVariant(status: PersonalSpacesStatus): BadgeVariant {
  switch (status) {
    case "active":
      return "success"
    case "paused_reconnect":
    case "purge_awaiting_approval":
    case "purge_deferred_stale":
    case "purge_suspended":
    case "pending":
      return "warning"
    case "purge_blocked_hold":
      return "error"
    case "hidden":
    case "purging":
    case "purged":
      return "secondary"
    default:
      return "outline"
  }
}

const UNITS = ["B", "KB", "MB", "GB", "TB"] as const

export function formatPersonalSpacesBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B"
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit += 1
  }
  const digits = unit === 0 || value >= 100 ? 0 : 1
  return `${value.toFixed(digits)} ${UNITS[unit]}`
}

/** Index time (summed seconds) as a short duration: 45 s, 12 min, 3 h 05 min. */
export function formatPersonalSpacesDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0 s"
  if (seconds < 60) return `${Math.round(seconds)} s`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  return `${hours} h ${String(minutes % 60).padStart(2, "0")} min`
}

/** Freshness lag (p95, seconds behind the provider); null means nothing indexed yet. */
export function formatPersonalSpacesLag(seconds: number | null): string {
  return seconds === null ? "" : formatPersonalSpacesDuration(seconds)
}

/** The skip counters of a row, largest first, zero counts left out. */
export function personalSpacesSkipEntries(
  skips: PersonalSpacesRow["skips_by_reason"]
): { reason: PersonalSpacesSkipReason; count: number }[] {
  return PERSONAL_SPACES_SKIP_REASONS.flatMap((reason) => {
    const count = skips[reason] ?? 0
    return count > 0 ? [{ reason, count }] : []
  }).sort((a, b) => b.count - a.count)
}

export function filterPersonalSpacesRows(
  rows: PersonalSpacesRow[],
  query: string
): PersonalSpacesRow[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return rows
  return rows.filter(
    (row) =>
      row.display_name.toLowerCase().includes(needle) || row.user_id.toLowerCase().includes(needle)
  )
}

export function personalSpacesCsvFilename(
  source: PersonalSpacesSource | undefined,
  now: Date
): string {
  const stamp = now.toISOString().slice(0, 10).replaceAll("-", "")
  return `personal-sources-${source ?? "all"}-${stamp}.csv`
}
